import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ACTION,
  MONEY,
  PhoneInput,
  SHIFT,
  formatCreditsWithUnit,
  formatInTimezone,
  shiftStatus,
  useTimezone,
} from '@kioskgaming/ui';
import CashierLayout from '../components/CashierLayout';
import { useCashierAuth } from '../hooks/useCashierAuth';
import {
  cashierCreatePlayer,
  cashierDepositCredits,
  cashierFetchPlayers,
  cashierGetShift,
  cashierListTransactions,
  cashierRedeemCredits,
  CashierShift,
  CashierTransaction,
  DeviceRef,
} from '../services/cashierApi';
import { formatCurrency } from '../utils/denominations';
import { describeDeviceError, shiftDeviceNameFromError } from '../utils/deviceErrors';
import { canJoinShift, isShiftViewable, shiftNotViewableMessage } from '../utils/shiftAccess';
import {
  formatTxType,
  getTxStatus,
  txAmountClass,
  txStatusBadgeClass,
  txStatusLabel,
} from '../utils/transactionDisplay';

type PlayerRow = {
  id: string;
  phone?: string | null;
  username?: string | null;
  balance?: number;
};

export default function ActiveShiftPage() {
  const { id } = useParams<{ id: string }>();
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const { device, refreshMe, logout } = useCashierAuth();
  const [shift, setShift] = useState<CashierShift | null>(null);
  /** The device the shift runs on (from GET /cashier/shifts/:id). */
  const [shiftDevice, setShiftDevice] = useState<DeviceRef | null>(null);
  /** The server refused a money operation from this device (WRONG_DEVICE / DEVICE_UNKNOWN). */
  const [refusedCode, setRefusedCode] = useState<'WRONG_DEVICE' | 'DEVICE_UNKNOWN' | null>(null);
  const [transactions, setTransactions] = useState<CashierTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [amount, setAmount] = useState<Record<string, string>>({});
  const [cashPaid, setCashPaid] = useState<Record<string, string>>({});
  const [cashPaidTouched, setCashPaidTouched] = useState<Record<string, boolean>>({});

  const [createOpen, setCreateOpen] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);

  const loadShift = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [shiftRes, txRes] = await Promise.all([
        cashierGetShift(id),
        cashierListTransactions(id),
      ]);
      if (!shiftRes.success || !shiftRes.data?.shift) {
        toast.error(shiftRes.message || shiftRes.code || 'Shift not found');
        setShift(null);
        return;
      }
      const loadedShift = shiftRes.data.shift;
      if (!isShiftViewable(loadedShift.status)) {
        toast.error(shiftNotViewableMessage(loadedShift.status));
        setShift(null);
        navigate('/', { replace: true });
        return;
      }
      setShift(loadedShift);
      setShiftDevice(shiftRes.data.device ?? null);
      setTransactions(txRes.success ? txRes.data || [] : shiftRes.data.transactions || []);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    setLoadingPlayers(true);
    try {
      const res = await cashierFetchPlayers({ search: search.trim() || undefined, limit: 20, page: 1 });
      setPlayers((res.data || []) as PlayerRow[]);
    } catch {
      setPlayers([]);
    } finally {
      setLoadingPlayers(false);
    }
  };

  useEffect(() => {
    void loadShift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const t = setTimeout(() => void loadPlayers(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (!id) return <Navigate to="/" replace />;

  if (!loading && !shift) return <Navigate to="/" replace />;

  if (!loading && shift?.status === 'scheduled') {
    return <Navigate to={`/shifts/${id}/start`} replace />;
  }

  const isActive = !!shift && canJoinShift(shift.status);
  // Money operations only work on the device the shift runs on (strict device lock on the server).
  const onShiftDevice = !!device && !!shiftDevice && device.id === shiftDevice.id && !refusedCode;
  const wrongDevice = isActive && !onShiftDevice;
  const canOperate = isActive && onShiftDevice;
  const needsSignIn = wrongDevice && (!device || refusedCode === 'DEVICE_UNKNOWN');

  /** WRONG_DEVICE / DEVICE_UNKNOWN: switch to the read-only view with fresh device data. */
  const handleDeviceRefusal = async (res: { code?: string; data?: unknown }): Promise<boolean> => {
    if (res.code !== 'WRONG_DEVICE' && res.code !== 'DEVICE_UNKNOWN') return false;
    toast.error(describeDeviceError(res.code, { deviceName: shiftDeviceNameFromError(res.data) })!.message);
    setRefusedCode(res.code);
    await Promise.all([refreshMe(), loadShift()]);
    return true;
  };

  const shiftSubtitle = (() => {
    if (!shift) return undefined;
    if (canJoinShift(shift.status) && shift.openedAt) {
      return `Active since ${formatInTimezone(shift.openedAt, timezone)}`;
    }
    if (shift.status) {
      return `Shift status: ${shiftStatus(shift.status).label}`;
    }
    return undefined;
  })();

  const doCreatePlayer = async () => {
    if (!newPhone.trim() && !newEmail.trim()) {
      toast.error('Phone or email is required');
      return;
    }
    setCreating(true);
    try {
      const res = await cashierCreatePlayer({
        phone: newPhone.trim() || undefined,
        email: newEmail.trim() || undefined,
      });
      if (!res.success) {
        toast.error(res.message || res.code || 'Create Player failed');
        return;
      }
      toast.success('Player created');
      setCreateOpen(false);
      setNewPhone('');
      setNewEmail('');
      await loadPlayers();
    } catch (e: unknown) {
      toast.error((e as any)?.response?.data?.message || 'Create Player failed');
    } finally {
      setCreating(false);
    }
  };

  const doDeposit = async (playerId: string) => {
    const v = Number(amount[playerId]);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Amount must be > 0');
      return;
    }
    const res = await cashierDepositCredits(playerId, { amount: v });
    if (!res.success) {
      if (await handleDeviceRefusal(res)) return;
      toast.error(res.message || res.code || 'Deposit failed');
      return;
    }
    toast.success('Deposit completed');
    setAmount((p) => ({ ...p, [playerId]: '' }));
    await Promise.all([loadPlayers(), loadShift()]);
  };

  const doRedeem = async (playerId: string) => {
    const v = Number(amount[playerId]);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Credit amount must be > 0');
      return;
    }
    const cashRaw = cashPaid[playerId];
    const cash = cashRaw != null && cashRaw !== '' ? Number(cashRaw) : v;
    if (!Number.isFinite(cash) || cash <= 0) {
      toast.error('Cash paid must be > 0');
      return;
    }
    const res = await cashierRedeemCredits(playerId, { amount: v, cash_paid: cash });
    if (!res.success) {
      if (await handleDeviceRefusal(res)) return;
      toast.error(res.message || res.code || 'Redeem failed');
      return;
    }
    if (res.data?.pendingApproval) {
      toast('Redemption sent to your manager for approval. Do not pay cash to the Player.', {
        icon: '⏳',
        duration: 6000,
      });
      await loadShift();
    } else {
      toast.success('Redeem completed');
      await Promise.all([loadPlayers(), loadShift()]);
    }
    setAmount((p) => ({ ...p, [playerId]: '' }));
    setCashPaid((p) => ({ ...p, [playerId]: '' }));
    setCashPaidTouched((p) => ({ ...p, [playerId]: false }));
  };

  const pageTitle = shift && canJoinShift(shift.status) ? 'Active shift' : 'Shift';

  return (
    <CashierLayout title={pageTitle} subtitle={shiftSubtitle} backTo="/">
      {loading ? (
        <div className="text-sm text-stone-600">Loading…</div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {wrongDevice ? (
            <div role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Your shift is running on {shiftDevice?.name || 'another device'}. Deposits, redemptions, cash drops and
              ending the shift only work there.
              {needsSignIn ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span>This device is not recognized. Sign in again.</span>
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      navigate('/login', { replace: true });
                    }}
                    className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold hover:bg-amber-100"
                  >
                    Sign in again
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          {!isActive && shift ? (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              This shift is not open for deposits or redemptions
              {shift.status ? ` (${shiftStatus(shift.status).label})` : ''}.
              You can still look up and create Players.
            </div>
          ) : null}
          <div className="mb-4 flex flex-wrap gap-2">
            {canOperate ? (
              <>
                <Link
                  to={`/shifts/${id}/cash-drop`}
                  className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                >
                  {SHIFT.cashDrop}
                </Link>
                <Link
                  to={`/shifts/${id}/closing`}
                  className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
                >
                  {SHIFT.endShift}
                </Link>
                <Link
                  to={`/shifts/${id}/handover`}
                  className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                >
                  {SHIFT.handover}
                </Link>
              </>
            ) : null}
          </div>

          <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-2">
            <div className="flex min-h-0 flex-col rounded-xl border border-stone-200 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-stone-900">Player lookup</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    onClick={() => setCreateOpen(true)}
                  >
                    Create Player
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                    onClick={() => void loadPlayers()}
                  >
                    {ACTION.refresh}
                  </button>
                </div>
              </div>
              <div className="mt-3">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  placeholder="Search by phone / username"
                />
              </div>
              {canOperate ? (
                <p className="mt-2 text-xs text-stone-500">
                  Redemptions above the Shop limit need your manager's approval. Do not pay cash until approved.
                </p>
              ) : null}
              <div className="mt-3 min-h-0 flex-1 space-y-3 overflow-y-auto">
                {loadingPlayers ? (
                  <div className="text-sm text-stone-600">Loading…</div>
                ) : players.length === 0 ? (
                  <div className="text-sm text-stone-600">No Players found.</div>
                ) : (
                  players.map((p) => (
                    <div key={p.id} className="rounded-lg border border-stone-200 p-3">
                      <div className="truncate text-sm font-semibold text-stone-900">
                        {p.username || p.phone || p.id}
                      </div>
                      {wrongDevice ? null : (
                        <div className="mt-3 grid gap-2 md:grid-cols-2">
                          <input
                            value={amount[p.id] || ''}
                            onChange={(e) => {
                              const next = e.target.value;
                              setAmount((prev) => ({ ...prev, [p.id]: next }));
                              if (!cashPaidTouched[p.id]) {
                                setCashPaid((prev) => ({ ...prev, [p.id]: next }));
                              }
                            }}
                            className="w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                            placeholder="Credits"
                            disabled={!canOperate}
                          />
                          <input
                            value={cashPaid[p.id] ?? amount[p.id] ?? ''}
                            onChange={(e) => {
                              setCashPaidTouched((prev) => ({ ...prev, [p.id]: true }));
                              setCashPaid((prev) => ({ ...prev, [p.id]: e.target.value }));
                            }}
                            className="w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                            placeholder="Cash paid (1:1 pre-fill)"
                            disabled={!canOperate}
                          />
                          <button
                            type="button"
                            onClick={() => void doDeposit(p.id)}
                            className="w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 md:col-span-1"
                            disabled={!canOperate}
                          >
                            {MONEY.deposit}
                          </button>
                          <button
                            type="button"
                            onClick={() => void doRedeem(p.id)}
                            className="w-full rounded-lg bg-stone-800 px-3 py-2 text-sm font-semibold text-white hover:bg-stone-900 disabled:opacity-60 md:col-span-1"
                            disabled={!canOperate}
                          >
                            {MONEY.redeem}
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="flex min-h-0 flex-col rounded-xl border border-stone-200 bg-white p-4">
              <div className="text-sm font-semibold text-stone-900">Shift transactions</div>
              {shift?.openingCashBalance != null ? (
                <div className="mt-1 text-xs text-stone-600">
                  Opening cash: {formatCurrency(Number(shift.openingCashBalance))}
                </div>
              ) : null}
              <div className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto">
                {transactions.length === 0 ? (
                  <div className="text-sm text-stone-600">No transactions yet.</div>
                ) : (
                  transactions.map((tx) => {
                    const status = getTxStatus(tx);
                    const isPendingRedeem = tx.type === 'redeem_credits' && status === 'pending_approval';
                    return (
                      <div key={tx.id} className="flex items-center justify-between rounded-lg border border-stone-100 px-3 py-2 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-stone-900">{formatTxType(tx.type)}</span>
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${txStatusBadgeClass(status)}`}
                            >
                              {txStatusLabel(status)}
                            </span>
                          </div>
                          <div className="text-xs text-stone-500">
                            {tx.createdAt ? formatInTimezone(tx.createdAt, timezone) : ''}
                          </div>
                          {isPendingRedeem ? (
                            <div className="mt-0.5 text-xs font-medium text-amber-700">
                              Do not pay cash until approved
                            </div>
                          ) : null}
                        </div>
                        <div className={`ml-3 shrink-0 font-semibold ${txAmountClass(tx.type, status)}`}>
                          {tx.type === 'redeem_credits' || tx.type === 'deposit_credits' ? (
                            // Credits never carry $; the cash that changed hands is its own line.
                            <div className="text-right">
                              <div>{formatCreditsWithUnit(tx.amount)}</div>
                              <div className="text-xs font-medium text-stone-500">
                                {tx.metadata?.cashPaid != null
                                  ? `Cash ${formatCurrency(Number(tx.metadata.cashPaid))}`
                                  : tx.cashDelta != null && Number(tx.cashDelta) !== 0
                                    ? `Cash ${formatCurrency(Math.abs(Number(tx.cashDelta)))}`
                                    : null}
                              </div>
                            </div>
                          ) : (
                            tx.amount != null ? formatCurrency(Number(tx.amount)) : '—'
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {createOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Create Player</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700">Phone</label>
                <div className="mt-1 transfer-phone-field">
                  <PhoneInput
                    value={newPhone}
                    onChange={setNewPhone}
                    defaultCountry="US"
                    placeholder="Phone number"
                    disabled={creating}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Email</label>
                <input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  placeholder="player@example.com"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={() => setCreateOpen(false)}
                disabled={creating}
              >
                {ACTION.cancel}
              </button>
              <button
                type="button"
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                onClick={() => void doCreatePlayer()}
                disabled={creating}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </CashierLayout>
  );
}
